// revenueTypesManagement.js - Revenue Types Management Module
import { supabase } from './supabaseClient.js';
import { showStatus, formatCurrency } from './utils.js';

export const RevenueTypesManagement = {
    // Cache DOM elements
    elements: {},
    
    // Initialize the module
    init() {
        // Cache elements
        this.elements = {
            modal: document.getElementById('revenue-types-modal'),
            manageBtn: document.getElementById('manage-revenue-types-btn') || document.getElementById('manage-rev-types-btn'),
            closeModalBtn: document.getElementById('close-revenue-types-modal'),
            addBtn: document.getElementById('add-revenue-type-btn'),
            form: document.getElementById('revenue-type-form'),
            formSection: document.getElementById('revenue-type-form-section'),
            formTitle: document.getElementById('revenue-type-form-title'),
            cancelFormBtn: document.getElementById('cancel-revenue-type-form'),
            tableBody: document.getElementById('revenue-types-table-body'),
            // Form inputs
            idInput: document.getElementById('revenue-type-id'),
            nameInput: document.getElementById('revenue-type-name'),
            descriptionInput: document.getElementById('revenue-type-description'),
            amountInput: document.getElementById('revenue-type-amount'),
            categorySelect: document.getElementById('revenue-type-category')
        };
        
        // Set up event listeners
        this.setupEventListeners();
        
        console.log('RevenueTypesManagement module initialized');
    },
    
    // Set up all event listeners
    setupEventListeners() {
        // Open modal
        this.elements.manageBtn?.addEventListener('click', () => this.openModal());
        
        // Close modal
        this.elements.closeModalBtn?.addEventListener('click', () => this.closeModal());
        this.elements.modal?.addEventListener('click', (e) => {
            if (e.target === this.elements.modal) this.closeModal();
        });
        
        // Form actions
        this.elements.addBtn?.addEventListener('click', () => this.showForm());
        this.elements.cancelFormBtn?.addEventListener('click', () => this.hideForm());
        this.elements.form?.addEventListener('submit', (e) => this.handleFormSubmit(e));
    },
    
    // Open the management modal
    async openModal() {
        this.elements.modal?.classList.remove('hidden');
        await this.loadRevenueTypes();
    },
    
    // Close the modal
    closeModal() {
        this.elements.modal?.classList.add('hidden');
        this.hideForm();
    },
    
    // Show the form
    showForm(revenueType = null) {
        this.elements.formSection?.classList.remove('hidden');
        
        if (revenueType) {
            // Edit mode
            this.elements.formTitle.textContent = 'Modifier le type de recette';
            this.elements.idInput.value = revenueType.id;
            this.elements.nameInput.value = revenueType.name;
            this.elements.descriptionInput.value = revenueType.description || '';
            this.elements.amountInput.value = revenueType.amount;
            this.elements.categorySelect.value = revenueType.category;
        } else {
            // Add mode
            this.elements.formTitle.textContent = 'Nouveau type de recette';
            this.elements.form.reset();
            this.elements.idInput.value = '';
            this.elements.categorySelect.value = 'accueil';
        }
    },
    
    // Hide the form
    hideForm() {
        this.elements.formSection?.classList.add('hidden');
        this.elements.form?.reset();
    },
    
    // Load and display revenue types
    async loadRevenueTypes() {
        this.elements.tableBody.innerHTML = '<tr><td colspan="5" class="px-4 py-3 text-center text-gray-500">Loading revenue types...</td></tr>';
        
        try {
            const { data: revenueTypes, error } = await supabase
                .from('revenue_types')
                .select('*')
                .order('name', { ascending: true });
            
            if (error) throw error;
            
            if (revenueTypes && revenueTypes.length > 0) {
                this.displayRevenueTypes(revenueTypes);
            } else {
                this.elements.tableBody.innerHTML = '<tr><td colspan="5" class="px-4 py-3 text-center text-gray-500">No revenue types found</td></tr>';
            }
        } catch (error) {
            console.error('Error loading revenue types:', error);
            showStatus('Failed to load revenue types: ' + error.message, true);
            this.elements.tableBody.innerHTML = '<tr><td colspan="5" class="px-4 py-3 text-center text-red-500">Error loading revenue types</td></tr>';
        }
    },
    
    // Display revenue types in the table
    displayRevenueTypes(revenueTypes) {
        this.elements.tableBody.innerHTML = '';
        
        // Group by category
        const groupedTypes = revenueTypes.reduce((acc, type) => {
            if (!acc[type.category]) acc[type.category] = [];
            acc[type.category].push(type);
            return acc;
        }, {});
        
        Object.entries(groupedTypes).forEach(([category, types]) => {
            // Add category header
            const categoryRow = document.createElement('tr');
            categoryRow.className = 'bg-gray-100';
            categoryRow.innerHTML = `
                <td colspan="5" class="px-4 py-2 font-semibold text-gray-700">
                    ${category.toUpperCase().replace('_', ' ')} (${types.length} items)
                </td>
            `;
            this.elements.tableBody.appendChild(categoryRow);
            
            // Add revenue types for this category
            types.forEach(type => {
                const row = document.createElement('tr');
                row.className = 'border-b hover:bg-gray-50';
                
                row.innerHTML = `
                    <td class="px-4 py-3 text-sm">${type.name}</td>
                    <td class="px-4 py-3 text-sm text-gray-600">${type.description || '-'}</td>
                    <td class="px-4 py-3 text-sm font-medium">${formatCurrency(type.amount)}</td>
                    <td class="px-4 py-3 text-sm">
                        <span class="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            ${type.category}
                        </span>
                    </td>
                    <td class="px-4 py-3 text-center">
                        <button onclick="RevenueTypesManagement.editRevenueType('${type.id}')" class="btn-edit">Edit</button>
                        <button onclick="RevenueTypesManagement.deleteRevenueType('${type.id}', '${type.name.replace(/'/g, "\\'")}')" class="btn-delete">Delete</button>
                    </td>
                `;
                
                this.elements.tableBody.appendChild(row);
            });
        });
    },
    
    // Handle form submission
    async handleFormSubmit(e) {
        e.preventDefault();
        
        const id = this.elements.idInput.value;
        const name = this.elements.nameInput.value.trim();
        const description = this.elements.descriptionInput.value.trim();
        const amount = parseFloat(this.elements.amountInput.value);
        const category = this.elements.categorySelect.value;
        
        // Validation
        if (!name || amount <= 0) {
            showStatus('Please provide a valid name and amount', true);
            return;
        }
        
        if (id) {
            // Update existing
            await this.updateRevenueType(id, { name, description, amount, category });
        } else {
            // Create new
            await this.createRevenueType({ name, description, amount, category });
        }
    },
    
    // Create a new revenue type
    async createRevenueType(data) {
        try {
            showStatus('Creating revenue type...', false);
            
            const { error } = await supabase
                .from('revenue_types')
                .insert([data]);
            
            if (error) throw error;
            
            showStatus('Revenue type created successfully!', false);
            this.hideForm();
            await this.loadRevenueTypes();
            
            // Refresh the main display too
            if (window.fetchAndDisplayRevenueTypes) {
                window.fetchAndDisplayRevenueTypes();
            }
            
        } catch (error) {
            console.error('Error creating revenue type:', error);
            showStatus('Failed to create revenue type: ' + error.message, true);
        }
    },
    
    // Update existing revenue type
    async updateRevenueType(id, data) {
        try {
            showStatus('Updating revenue type...', false);
            
            const { error } = await supabase
                .from('revenue_types')
                .update(data)
                .eq('id', id);
            
            if (error) throw error;
            
            showStatus('Revenue type updated successfully!', false);
            this.hideForm();
            await this.loadRevenueTypes();
            
            // Refresh the main display too
            if (window.fetchAndDisplayRevenueTypes) {
                window.fetchAndDisplayRevenueTypes();
            }
            
        } catch (error) {
            console.error('Error updating revenue type:', error);
            showStatus('Failed to update revenue type: ' + error.message, true);
        }
    },
    
    // Edit revenue type
    async editRevenueType(id) {
        try {
            const { data: revenueType, error } = await supabase
                .from('revenue_types')
                .select('*')
                .eq('id', id)
                .single();
            
            if (error) throw error;
            
            this.showForm(revenueType);
        } catch (error) {
            console.error('Error fetching revenue type:', error);
            showStatus('Failed to load revenue type details', true);
        }
    },
    
    // Delete revenue type
    async deleteRevenueType(id, name) {
        if (!confirm(`Are you sure you want to delete the revenue type: ${name}?\n\nNote: This will not affect existing transactions.`)) {
            return;
        }
        
        try {
            showStatus('Deleting revenue type...', false);
            
            const { error } = await supabase
                .from('revenue_types')
                .delete()
                .eq('id', id);
            
            if (error) {
                if (error.code === '23503') {
                    throw new Error('Cannot delete: This revenue type is used in existing transactions.');
                }
                throw error;
            }
            
            showStatus('Revenue type deleted successfully!', false);
            await this.loadRevenueTypes();
            
            // Refresh the main display too
            if (window.fetchAndDisplayRevenueTypes) {
                window.fetchAndDisplayRevenueTypes();
            }
            
        } catch (error) {
            console.error('Error deleting revenue type:', error);
            showStatus('Failed to delete revenue type: ' + error.message, true);
        }
    }
};

// Make RevenueTypesManagement available globally for onclick handlers
window.RevenueTypesManagement = RevenueTypesManagement;